import axios from "axios";
import { getStoredLeadId } from "./leadIdCookie";

export const TRIAL_BOT_TYPES = {
  ROOFING: "roofing",
  RENOVATION: "renovation",
  REAL_ESTATE: "real_estate",
  UNIVERSAL: "universal",
};

export const UNIVERSAL_SIGNUP_SUCCESS_MESSAGE =
  "Your account is ready. If a welcome email arrives, it contains the password. If it does not, use password reset.";

export const UNIVERSAL_SIGNUP_DASHBOARD_ROUTE = "/dashboard";

export const SIGNUP_ACCOUNT_RECOVERY_LINKS = Object.freeze({
  login: "/login",
  passwordReset: "/password-reset",
});

const SIGNUP_FAILURE_CODES = new Set([
  "NICHE_TEMPLATE_UNAVAILABLE",
  "SIGNUP_ROLLED_BACK",
  "ACCOUNT_SETUP_INCOMPLETE",
]);

const SIGNUP_LATER_SUBMIT_CODES = new Set([
  "NICHE_TEMPLATE_UNAVAILABLE",
  "SIGNUP_ROLLED_BACK",
]);

export const TRIAL_DUPLICATE_EMAIL_MESSAGE =
  "This email is already in use. Please use a different email or log in.";

export async function registerTrialAccount({ email, botType }) {
  const normalizedEmail = email.trim().toLowerCase();
  const response = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/api/auth/register-account-trial`,
    {
      email: normalizedEmail,
      bot_type: botType,
      shock_marketing_lead_id: getStoredLeadId() || undefined,
    }
  );
  return response.data;
}

export async function registerUniversalTrialAccount({ email, nicheId }) {
  const normalizedEmail = email.trim().toLowerCase();
  const response = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/api/auth/register-account-trial`,
    {
      email: normalizedEmail,
      bot_type: TRIAL_BOT_TYPES.UNIVERSAL,
      niche_id: nicheId,
      shock_marketing_lead_id: getStoredLeadId() || undefined,
    }
  );
  return response.data;
}

export function realEstateSignupSuccessMessage(email) {
  return `We sent login details to ${email}. Check your inbox for your password.`;
}

function formatDetailItem(item) {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return "";
  const loc = Array.isArray(item.loc) ? item.loc.map((part) => String(part)).join(".") : "";
  const msg = typeof item.msg === "string" ? item.msg : "";
  if (loc && msg) return `${loc}: ${msg}`;
  return msg || loc;
}

function formatValidationDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(formatDetailItem).filter(Boolean).join("; ");
  }
  if (detail && typeof detail === "object") return formatDetailItem(detail);
  return "";
}

export function mapTrialSignupError(err, duplicateMessage = TRIAL_DUPLICATE_EMAIL_MESSAGE) {
  const data = err?.response?.data;
  if (data?.message === "The user already exists") {
    return duplicateMessage;
  }
  const code = typeof data?.code === "string" ? data.code : "";
  if (SIGNUP_FAILURE_CODES.has(code)) {
    return typeof data.message === "string"
      ? data.message
      : "An unexpected error occurred. Please try again.";
  }
  if (data && Object.prototype.hasOwnProperty.call(data, "detail")) {
    const text = formatValidationDetail(data.detail);
    if (text) return text;
  }
  if (typeof data?.message === "string" && data.message) {
    return data.message;
  }
  if (err?.request) {
    return "Network error. Please check your connection and try again.";
  }
  return "An unexpected error occurred. Please try again.";
}

export function trialSignupFailureGuide(err) {
  const data = err?.response?.data;
  const code = typeof data?.code === "string" ? data.code : null;
  const network = !err?.response && Boolean(err?.request);
  const showRecovery = network || code === "ACCOUNT_SETUP_INCOMPLETE";
  return {
    message: mapTrialSignupError(err),
    code,
    links: showRecovery
      ? [SIGNUP_ACCOUNT_RECOVERY_LINKS.login, SIGNUP_ACCOUNT_RECOVERY_LINKS.passwordReset]
      : [],
    allowCookies: false,
    allowManualSubmit: SIGNUP_LATER_SUBMIT_CODES.has(code),
    repeatPost: false,
  };
}
